/**
 * QP-150 advisor navigation — the tab "vocabulary" for the unified goal/portfolio nav.
 *
 * A single tab row (the "goal context row") is heterogeneous: it can hold goal-page tabs
 * alongside portfolio-instance tabs (multiPortfolio on) or portfolio-page tabs (multiPortfolio
 * off). To keep one `<Tabs value=...>` scheme unambiguous, every entry encodes its kind into a
 * prefixed string value and decodes back:
 *
 *   - goal page              → `gp:<pageId>`        (before OR after the portfolios — QL-117:
 *                                                    the position is a property of the page, not
 *                                                    of the selection, so one kind covers both)
 *   - portfolio instance     → `pf:<portfolioId>`   (multiPortfolio on)
 *   - portfolio page direct  → `pp:<pageId>`        (multiPortfolio off — single portfolio)
 *
 * Pure helpers, no React — safe to import from any nav component.
 */

export type GoalNavEntry =
  | { kind: "goalPage"; pageId: string }
  | { kind: "portfolio"; portfolioId: string }
  | { kind: "portfolioPageDirect"; pageId: string };

/** Encode a nav entry into a unique `<prefix>:<id>` tab value. */
export function encodeGoalNavEntry(entry: GoalNavEntry): string {
  switch (entry.kind) {
    case "goalPage":
      return `gp:${entry.pageId}`;
    case "portfolio":
      return `pf:${entry.portfolioId}`;
    case "portfolioPageDirect":
      return `pp:${entry.pageId}`;
  }
}

/** Decode a `<prefix>:<id>` tab value back into a nav entry, or `null` if unrecognized. */
export function decodeGoalNavEntry(value: string): GoalNavEntry | null {
  const sep = value.indexOf(":");
  if (sep === -1) return null;
  const prefix = value.slice(0, sep);
  const id = value.slice(sep + 1);
  if (!id) return null;
  switch (prefix) {
    case "gp":
      return { kind: "goalPage", pageId: id };
    case "pf":
      return { kind: "portfolio", portfolioId: id };
    case "pp":
      return { kind: "portfolioPageDirect", pageId: id };
    default:
      return null;
  }
}

/** True when two nav entries point at the same tab. */
export function goalNavEntriesEqual(a: GoalNavEntry | null, b: GoalNavEntry | null): boolean {
  if (a === null || b === null) return a === b;
  return encodeGoalNavEntry(a) === encodeGoalNavEntry(b);
}
